import React from 'react';
import { Progress } from 'antd';
import notification from 'utils/notificationBridge';
import { PUBLISH_CONTENT_TYPES } from 'helpers/publishContentTypes';

const PHASE_LABELS = {
  [PUBLISH_CONTENT_TYPES.BANNER]: {
    queued: 'Publishing banner…',
    'uploading-web-image': 'Uploading web image…',
    'uploading-mobile-image': 'Uploading mobile image…',
    creating: 'Saving banner…',
    updating: 'Updating banner…',
  },
  [PUBLISH_CONTENT_TYPES.TEMPLATE]: {
    queued: 'Publishing template…',
    'uploading-fcm-image': 'Uploading FCM image…',
    'uploading-whatsapp-image': 'Uploading WhatsApp image…',
    creating: 'Saving template…',
    updating: 'Updating template…',
  },
  [PUBLISH_CONTENT_TYPES.PAGES]: {
    queued: 'Publishing page…',
    'uploading-image': 'Uploading image…',
    'uploading-file': 'Uploading file…',
    creating: 'Saving page…',
    updating: 'Updating page…',
  },
  [PUBLISH_CONTENT_TYPES.CAREERS]: {
    queued: 'Publishing career…',
    creating: 'Saving career…',
    updating: 'Updating career…',
  },
  [PUBLISH_CONTENT_TYPES.CAREER_APPLICATIONS]: {
    queued: 'Publishing career application…',
    'uploading-resume': 'Uploading resume…',
    creating: 'Saving career application…',
    updating: 'Updating career application…',
  },
  [PUBLISH_CONTENT_TYPES.OUR_TEAM]: {
    queued: 'Publishing team member…',
    'uploading-image': 'Uploading image…',
    creating: 'Saving team member…',
    updating: 'Updating team member…',
  },
  [PUBLISH_CONTENT_TYPES.PROJECTS]: {
    queued: 'Publishing project…',
    'uploading-image': 'Uploading image…',
    creating: 'Saving project…',
    updating: 'Updating project…',
  },
  [PUBLISH_CONTENT_TYPES.CATEGORIES]: {
    queued: 'Publishing category…',
    creating: 'Saving category…',
    updating: 'Updating category…',
  },
  [PUBLISH_CONTENT_TYPES.WIDGETS]: {
    queued: 'Publishing widget…',
    creating: 'Saving widget…',
    updating: 'Updating widget…',
  },
  [PUBLISH_CONTENT_TYPES.SEO]: {
    queued: 'Publishing SEO entry…',
    creating: 'Saving SEO entry…',
    updating: 'Updating SEO entry…',
  },
  [PUBLISH_CONTENT_TYPES.INFORMATION_POLICY]: {
    queued: 'Publishing information policy…',
    creating: 'Saving information policy…',
    updating: 'Updating information policy…',
  },
  [PUBLISH_CONTENT_TYPES.ADMIN_SETTINGS]: {
    queued: 'Publishing settings…',
    'uploading-logo': 'Uploading logo…',
    'uploading-favicon': 'Uploading favicon…',
    creating: 'Saving settings…',
    updating: 'Updating settings…',
  },
  [PUBLISH_CONTENT_TYPES.CONFIG_SETTINGS]: {
    queued: 'Publishing config settings…',
    creating: 'Saving config settings…',
    updating: 'Updating config settings…',
  },
};

const DEFAULT_TITLES = {
  [PUBLISH_CONTENT_TYPES.BANNER]: 'Banner',
  [PUBLISH_CONTENT_TYPES.TEMPLATE]: 'Template',
  [PUBLISH_CONTENT_TYPES.PAGES]: 'Page',
  [PUBLISH_CONTENT_TYPES.CAREERS]: 'Career',
  [PUBLISH_CONTENT_TYPES.CAREER_APPLICATIONS]: 'Career Application',
  [PUBLISH_CONTENT_TYPES.OUR_TEAM]: 'Team Member',
  [PUBLISH_CONTENT_TYPES.PROJECTS]: 'Project',
  [PUBLISH_CONTENT_TYPES.CATEGORIES]: 'Category',
  [PUBLISH_CONTENT_TYPES.WIDGETS]: 'Widget',
  [PUBLISH_CONTENT_TYPES.SEO]: 'SEO',
  [PUBLISH_CONTENT_TYPES.INFORMATION_POLICY]: 'Information Policy',
  [PUBLISH_CONTENT_TYPES.ADMIN_SETTINGS]: 'Settings',
  [PUBLISH_CONTENT_TYPES.CONFIG_SETTINGS]: 'Config Settings',
};

const SUCCESS_MESSAGES = {
  [PUBLISH_CONTENT_TYPES.BANNER]: 'Banner added successfully.',
  [PUBLISH_CONTENT_TYPES.TEMPLATE]: 'Template added successfully.',
  [PUBLISH_CONTENT_TYPES.PAGES]: 'Page added successfully.',
  [PUBLISH_CONTENT_TYPES.CAREERS]: 'Career added successfully.',
  [PUBLISH_CONTENT_TYPES.CAREER_APPLICATIONS]: 'Career application added successfully.',
  [PUBLISH_CONTENT_TYPES.OUR_TEAM]: 'Team member added successfully.',
  [PUBLISH_CONTENT_TYPES.PROJECTS]: 'Project added successfully.',
  [PUBLISH_CONTENT_TYPES.CATEGORIES]: 'Category added successfully.',
  [PUBLISH_CONTENT_TYPES.WIDGETS]: 'Widget added successfully.',
  [PUBLISH_CONTENT_TYPES.SEO]: 'SEO entry added successfully.',
  [PUBLISH_CONTENT_TYPES.INFORMATION_POLICY]: 'Information policy added successfully.',
  [PUBLISH_CONTENT_TYPES.ADMIN_SETTINGS]: 'Settings saved successfully.',
  [PUBLISH_CONTENT_TYPES.CONFIG_SETTINGS]: 'Config settings saved successfully.',
};

const SUCCESS_MESSAGES_EDIT = {
  [PUBLISH_CONTENT_TYPES.BANNER]: 'Banner updated successfully.',
  [PUBLISH_CONTENT_TYPES.TEMPLATE]: 'Template updated successfully.',
  [PUBLISH_CONTENT_TYPES.PAGES]: 'Page updated successfully.',
  [PUBLISH_CONTENT_TYPES.CAREERS]: 'Career updated successfully.',
  [PUBLISH_CONTENT_TYPES.CAREER_APPLICATIONS]: 'Career application updated successfully.',
  [PUBLISH_CONTENT_TYPES.OUR_TEAM]: 'Team member updated successfully.',
  [PUBLISH_CONTENT_TYPES.PROJECTS]: 'Project updated successfully.',
  [PUBLISH_CONTENT_TYPES.CATEGORIES]: 'Category updated successfully.',
  [PUBLISH_CONTENT_TYPES.WIDGETS]: 'Widget updated successfully.',
  [PUBLISH_CONTENT_TYPES.SEO]: 'SEO entry updated successfully.',
  [PUBLISH_CONTENT_TYPES.INFORMATION_POLICY]: 'Information policy updated successfully.',
  [PUBLISH_CONTENT_TYPES.ADMIN_SETTINGS]: 'Settings updated successfully.',
  [PUBLISH_CONTENT_TYPES.CONFIG_SETTINGS]: 'Config settings updated successfully.',
};

const FAILED_MESSAGES = {
  [PUBLISH_CONTENT_TYPES.BANNER]: 'Failed to publish banner.',
  [PUBLISH_CONTENT_TYPES.TEMPLATE]: 'Failed to publish template.',
  [PUBLISH_CONTENT_TYPES.PAGES]: 'Failed to publish page.',
  [PUBLISH_CONTENT_TYPES.CAREERS]: 'Failed to publish career.',
  [PUBLISH_CONTENT_TYPES.CAREER_APPLICATIONS]: 'Failed to publish career application.',
  [PUBLISH_CONTENT_TYPES.OUR_TEAM]: 'Failed to publish team member.',
  [PUBLISH_CONTENT_TYPES.PROJECTS]: 'Failed to publish project.',
  [PUBLISH_CONTENT_TYPES.CATEGORIES]: 'Failed to publish category.',
  [PUBLISH_CONTENT_TYPES.WIDGETS]: 'Failed to publish widget.',
  [PUBLISH_CONTENT_TYPES.SEO]: 'Failed to publish SEO entry.',
  [PUBLISH_CONTENT_TYPES.INFORMATION_POLICY]: 'Failed to publish information policy.',
  [PUBLISH_CONTENT_TYPES.ADMIN_SETTINGS]: 'Failed to save settings.',
  [PUBLISH_CONTENT_TYPES.CONFIG_SETTINGS]: 'Failed to save config settings.',
};

const FAILED_MESSAGES_EDIT = {
  [PUBLISH_CONTENT_TYPES.BANNER]: 'Failed to update banner.',
  [PUBLISH_CONTENT_TYPES.TEMPLATE]: 'Failed to update template.',
  [PUBLISH_CONTENT_TYPES.PAGES]: 'Failed to update page.',
  [PUBLISH_CONTENT_TYPES.CAREERS]: 'Failed to update career.',
  [PUBLISH_CONTENT_TYPES.CAREER_APPLICATIONS]: 'Failed to update career application.',
  [PUBLISH_CONTENT_TYPES.OUR_TEAM]: 'Failed to update team member.',
  [PUBLISH_CONTENT_TYPES.PROJECTS]: 'Failed to update project.',
  [PUBLISH_CONTENT_TYPES.CATEGORIES]: 'Failed to update category.',
  [PUBLISH_CONTENT_TYPES.WIDGETS]: 'Failed to update widget.',
  [PUBLISH_CONTENT_TYPES.SEO]: 'Failed to update SEO entry.',
  [PUBLISH_CONTENT_TYPES.INFORMATION_POLICY]: 'Failed to update information policy.',
  [PUBLISH_CONTENT_TYPES.ADMIN_SETTINGS]: 'Failed to update settings.',
  [PUBLISH_CONTENT_TYPES.CONFIG_SETTINGS]: 'Failed to update config settings.',
};

const getPhaseLabel = (contentType, phase) =>
  PHASE_LABELS[contentType]?.[phase] ||
  PHASE_LABELS[contentType]?.queued ||
  'Publishing…';

export const getContentPublishNotificationKey = (jobId) =>
  `content-publish-${jobId}`;

export const openContentPublishNotification = ({
  jobId,
  title,
  contentType,
  phase,
  percent,
}) => {
  notification.open({
    key: getContentPublishNotificationKey(jobId),
    title: title || DEFAULT_TITLES[contentType] || 'Publishing',
    description: (
      <>
        <p style={{ margin: '0 0 8px' }}>{getPhaseLabel(contentType, phase)}</p>
        <Progress percent={percent} size="small" status="active" />
      </>
    ),
    duration: 0,
  });
};

export const closeContentPublishNotification = (jobId) => {
  notification.destroy(getContentPublishNotificationKey(jobId));
};

export const getPublishSuccessMessage = (contentType, mode) =>
  (mode === 'EDIT' ? SUCCESS_MESSAGES_EDIT : SUCCESS_MESSAGES)[contentType] ||
  (mode === 'EDIT' ? 'Updated successfully.' : 'Published successfully.');

export const getPublishFailedMessage = (contentType, mode) =>
  (mode === 'EDIT' ? FAILED_MESSAGES_EDIT : FAILED_MESSAGES)[contentType] ||
  (mode === 'EDIT' ? 'Update failed.' : 'Publish failed.');
